import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Pagination, styled } from '@mui/material';

import Spinner from 'ui-component/Spinner';

const StyledDataGrid = styled(DataGrid, {
    shouldForwardProp: (prop) => prop !== 'headerBgColor'
})(({ theme, headerBgColor }) => {
    const bgColor = headerBgColor || theme.palette.grey[200];
    return {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        // Target the CSS variable used by DataGrid v7 for header backgrounds
        '--DataGrid-containerBackground': `${bgColor} !important`,
        '& .MuiDataGrid-main': {
            flex: 1,
            overflow: 'hidden'
        },
        '& .MuiDataGrid-virtualScroller': {
            overflow: 'auto !important',
            overflowX: 'auto !important',
            overflowY: 'scroll !important'
        },
        '& .MuiDataGrid-virtualScrollerContent': {
            minHeight: 'auto !important'
        },
        '& .MuiDataGrid-virtualScrollerRenderZone': {
            position: 'relative'
        },
        '& .MuiDataGrid-columnHeaders': {
            backgroundColor: `${bgColor} !important`,
            borderTop: `1px solid ${theme.palette.grey[300]}`,
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            flexShrink: 0
        },
        '& .MuiDataGrid-columnHeaderRow': {
            backgroundColor: `${bgColor} !important`
        },
        '& .MuiDataGrid-filler': {
            backgroundColor: 'inherit !important'
        },
        '& .MuiDataGrid-columnHeader': {
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            backgroundColor: `${bgColor} !important`
        },
        '& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox': {
            minWidth: '50px !important',
            maxWidth: '50px !important',
            width: '50px !important',
            padding: '0 !important',
            justifyContent: 'center'
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            color: headerBgColor ? '#fff' : theme.palette.grey[800],
            fontWeight: 500
        },
        '& .MuiDataGrid-columnSeparator': {
            color: theme.palette.grey[200]
        },
        '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        '& .MuiDataGrid-cell:focus': {
            outline: 'none'
        },
        '& .MuiDataGrid-cell:focus-within': {
            outline: 'none'
        },
        '& .MuiDataGrid-row': {
            position: 'relative',
            overflow: 'hidden'
        },
        '& .MuiDataGrid-row:not(:last-child)': {
            borderBottom: `1px solid ${theme.palette.grey[300]}`
        },
        '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.background.paper,
            flexShrink: 0
        },
        '& .MuiDataGrid-row.muted': {
            background: theme.palette.grey[200]
        }
    };
});

export function CPagination({ totalCount, countPerPage, currentPage, onPageChange }) {
    return (
        <Box sx={{ my: 1 }}>
            <Pagination
                color="primary"
                showFirstButton
                showLastButton
                count={Math.ceil((totalCount || 1) / (countPerPage || 1))}
                page={currentPage}
                onChange={onPageChange}
            />
        </Box>
    );
}

const CTable = forwardRef((props, ref) => {
    const {
        loading,
        rows,
        cols,
        currentPage,
        totalCount,
        countPerPage,
        onPageChange,
        handleSortModelChange,
        pagination = true,
        rowHeight = 60,
        slots,
        autoHeight,
        disabledKeyName = '',
        initialState = {},
        headerBgColor,
        // Handle old prop name for backward compatibility
        onSelectionModelChange,
        ...rest
    } = props;

    // Create a custom pagination component using useMemo to prevent recreation
    const CustomPaginationComponent = React.useMemo(
        () =>
            function CustomPagination() {
                return (
                    <CPagination
                        totalCount={totalCount}
                        currentPage={currentPage}
                        countPerPage={countPerPage}
                        onPageChange={onPageChange}
                    />
                );
            },
        [totalCount, currentPage, countPerPage, onPageChange]
    );

    const gridSlots = {
        ...slots
    };

    // Only add pagination slot if pagination is enabled
    if (pagination) {
        gridSlots.pagination = CustomPaginationComponent;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            {loading && <Spinner />}
            <StyledDataGrid
                ref={ref}
                headerBgColor={headerBgColor}
                sortingMode="server"
                paginationMode="server"
                sx={{
                    flex: 1,
                    width: '100%',
                    ...(autoHeight && { height: 'auto', flex: 'none' })
                }}
                // loading={loading}
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                disableRowSelectionOnClick
                rows={rows}
                getRowId={(row) => row.id}
                columns={cols}
                {...(rowHeight === 'auto' ? { getRowHeight: () => 'auto' } : { rowHeight })}
                {...(!pagination && { hideFooter: true })}
                onSortModelChange={handleSortModelChange}
                {...(disabledKeyName && {
                    getRowClassName: (params) => (params?.row?.[disabledKeyName] ? '' : 'muted')
                })}
                slots={gridSlots}
                localeText={{ noRowsLabel: 'No Data Found' }}
                initialState={{
                    sorting: {
                        sortModel: Object.keys(initialState).length > 0 ? [initialState] : []
                    }
                }}
                {...(onSelectionModelChange && {
                    onRowSelectionModelChange: onSelectionModelChange
                })}
                {...rest}
            />
        </Box>
    );
});

CPagination.propTypes = {
    totalCount: PropTypes.number,
    countPerPage: PropTypes.number,
    currentPage: PropTypes.number,
    onPageChange: PropTypes.func
};

CTable.propTypes = {
    loading: PropTypes.bool,
    rows: PropTypes.array.isRequired,
    cols: PropTypes.array.isRequired,
    currentPage: PropTypes.number,
    totalCount: PropTypes.number,
    countPerPage: PropTypes.number,
    pagination: PropTypes.bool,
    onPageChange: PropTypes.func,
    rowHeight: PropTypes.number,
    slots: PropTypes.object,
    disabledKeyName: PropTypes.string,
    autoHeight: PropTypes.bool, // Boolean to control the table's height adjustment behavior.
    handleSortModelChange: PropTypes.func, // A function to handle changes to the column sort model.
    initialState: PropTypes.object, // The initial state for the table, typically includes the default sort column and order.
    headerBgColor: PropTypes.string // Custom background color for the table header
};

export default CTable;

export const colProps = {
    sortable: false,
    flex: 1,
    minWidth: 100
};
